import { Card, CardContent } from '@ovComponents/2-component/card/card';
import { Box, Typography, useTheme } from 'hephaestus/web';
import { useTranslation } from 'react-i18next';
import type { TradeFlowPipelineProps } from './tradeFlowPipeline.types';
import { stageDotColor } from './tradeFlowPipeline.utils';
import { TradeFlowPipelineSkeleton } from './tradeFlowPipelineSkeleton';

const TradeFlowPipeline = ({
  data,
  loading,
  error,
  onNavigate,
  asOfLabel,
}: TradeFlowPipelineProps) => {
  const theme = useTheme();
  const { t } = useTranslation(['portfolioTradingHub']);

  const header = (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'flex-start',
        justifyContent: 'space-between',
        mb: 1,
      }}
    >
      <Box>
        <Typography variant='headingSmall'>
          {t('tradePipeline.title')}
        </Typography>
        {asOfLabel && (
          <Typography variant='bodySmall' color='textSecondary'>
            {`${t('tradePipeline.asOfPrefix')} ${asOfLabel}`}
          </Typography>
        )}
      </Box>
      {/* TODO: overflow menu in follow-up */}
      <Typography
        variant='labelLarge'
        sx={{ cursor: 'pointer', color: theme.color.primary }}
        onClick={() => onNavigate?.({ type: 'operations_list' })}
        data-testid='trade-flow-pipeline-view-trades'
      >
        {t('tradePipeline.viewTrades')}
      </Typography>
    </Box>
  );

  if (loading) {
    return (
      <TradeFlowPipelineSkeleton
        stageCount={data.stages.length}
        header={header}
      />
    );
  }

  if (error) {
    const message = error instanceof Error ? error.message : String(error);
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: 1,
        }}
      >
        {header}
        <Card>
          <CardContent>
            <Typography
              variant='bodyMedium'
              sx={{ color: theme.color.negative, fontWeight: 500 }}
            >
              {`${t('tradePipeline.errorPrefix')} ${message}`}
            </Typography>
          </CardContent>
        </Card>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: 1,
      }}
    >
      {header}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: `repeat(${data.stages.length}, 1fr)`,
          gap: 1,
          alignItems: 'stretch',
        }}
      >
        {data.stages.map((stage) => (
          <Box
            key={stage.id}
            data-testid={`trade-flow-pipeline-card-${stage.id}`}
            sx={{ height: '100%' }}
          >
            <Card sx={{ height: '100%' }}>
              <CardContent
                sx={{
                  p: 1,
                  '&:last-child': { pb: 1 },
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'center',
                }}
              >
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0.5,
                    mb: 0.25,
                  }}
                >
                  <Box
                    sx={{
                      width: 8,
                      height: 8,
                      borderRadius: '50%',
                      flexShrink: 0,
                      backgroundColor: stageDotColor(
                        theme.color,
                        stage.colorKey,
                      ),
                    }}
                  />
                  <Typography
                    variant='labelSmall'
                    sx={{
                      textTransform: 'uppercase',
                      color: theme.color.onSurfaceVariant,
                    }}
                  >
                    {stage.title}
                  </Typography>
                </Box>
                <Typography variant='headingSmall'>
                  {stage.count.toLocaleString()}
                </Typography>
              </CardContent>
            </Card>
          </Box>
        ))}
      </Box>
    </Box>
  );
};

export default TradeFlowPipeline;
